import { useCallback, useMemo, useRef } from "react";
import { PressableScale } from "pressto";
import { useFocusEffect } from "@react-navigation/native";
import { Stack } from "expo-router";
import { useUnistyles, StyleSheet } from "react-native-unistyles";
import { BottomSheetModal } from "@gorhom/bottom-sheet";
import { useCameras } from "@src/features/cameras";
import { View, Text, ScreenContainer } from "@src/components";
import { Icon } from "@src/components/Icon";
import { DataStateView, EmptyStateProps } from "@src/components/screen-state";
import { getCameraKeyString, isLegacyAppCamera } from "@src/utils/key-parser";
import { TabsHeader } from "@src/components/TabsHeader";
import { groupCamerasBySite } from "@src/features/cameras/utils/camera-group";
import { useSelectedSiteStore } from "@src/stores/selected-site-store";
import { useSiteWideDrawer } from "@src/components/AppDrawer/SiteWideDrawerProvider.native";
import { SiteCard } from "@src/components/SiteCard/index.native";
import { UpcomingExceptions } from "@src/features/alarms/components/UpcomingExceptions";
import { ManageAlarmSettingsBottomSheet } from "@src/features/alarms/components/ManageAlarmSettingsBottomSheet";
import { ScrollView } from "react-native";

export function SiteWideAlarmsScreen() {
  const { data: cameras, refetch: refetchCameras, isLoading, isFetching, error } = useCameras();
  const { theme } = useUnistyles();
  const selectedSiteId = useSelectedSiteStore((s) => s.selectedSiteId);
  const setSelectedSiteId = useSelectedSiteStore((s) => s.setSelectedSiteId);
  const { openDrawer } = useSiteWideDrawer();
  const manageSheetRef = useRef<BottomSheetModal>(null);

  const siteGroups = useMemo(() => {
    if (!cameras) return [];
    return groupCamerasBySite(cameras, "site");
  }, [cameras]);

  const selectedSiteName = useMemo(() => {
    if (!selectedSiteId) return null;
    return siteGroups.find((g) => g.siteId === selectedSiteId)?.siteName ?? null;
  }, [selectedSiteId, siteGroups]);

  useFocusEffect(
    useCallback(() => {
      refetchCameras();
    }, [refetchCameras]),
  );

  const supportedCameras = useMemo(() => {
    if (!cameras) return [];
    return cameras.filter((camera) => !isLegacyAppCamera(camera));
  }, [cameras]);

  const cameraKeys = useMemo(() => {
    if (!selectedSiteId) return [];
    return supportedCameras
      .filter((c) => c.site.id === selectedSiteId)
      .map((c) => getCameraKeyString(c))
      .filter(Boolean);
  }, [supportedCameras, selectedSiteId]);

  const hasCamerasButOnlyLegacy = useMemo(() => {
    return cameras && cameras.length > 0 && supportedCameras.length === 0;
  }, [cameras, supportedCameras]);

  const handleHamburgerMenuPress = useCallback(() => {
    openDrawer();
  }, [openDrawer]);

  const handleManagePress = useCallback(() => {
    manageSheetRef.current?.present();
  }, []);

  const handleSiteCardPress = useCallback(
    (siteId: string) => {
      setSelectedSiteId(siteId);
    },
    [setSelectedSiteId],
  );

  const emptyState = useMemo((): EmptyStateProps => {
    if (hasCamerasButOnlyLegacy) {
      return {
        icon: "videoOff",
        title: "Upgrade device fleet",
        message:
          "The new and improved alarm schedule management system, including the AI Schedule Assistant, is only available for the new fleet of devices. Get in touch with your account manager to discuss upgrading your device fleet.",
      };
    }
    return {
      icon: "videoOff",
      title: "No cameras found",
      message: "There are no cameras available to configure alarm settings.",
    };
  }, [hasCamerasButOnlyLegacy]);

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      <ScreenContainer edges={["top"]}>
        <TabsHeader
          selectedSiteName={selectedSiteName}
          onHamburgerMenuPress={handleHamburgerMenuPress}
          showLogo={false}
          title="Alarm Settings"
          headerShadowVisible
        />
        <DataStateView
          data={cameras}
          isLoading={isLoading}
          isFetching={isFetching}
          error={error}
          refetch={refetchCameras}
          isEmpty={(allCameras) => {
            if (!allCameras) return false;
            const supported = allCameras.filter((camera) => !isLegacyAppCamera(camera));
            return supported.length === 0;
          }}
          loadingMessage="Loading alarms..."
          errorTitle="Unable to load alarms"
          errorMessage="Please check your connection and try again."
          emptyState={emptyState}
          showStaleDataBanner={false}
        >
          {() => (
            <>
              <ScrollView
                showsVerticalScrollIndicator={false}
                style={{ flex: 1 }}
                contentContainerStyle={{
                  paddingHorizontal: theme.spacing["3xl"],
                  flexGrow: 1,
                  paddingTop: theme.spacing["4xl"],
                  paddingBottom: theme.spacing["12xl"],
                }}
              >
                {selectedSiteId ?
                  <View gap="7xl">
                    <PressableScale onPress={handleManagePress}>
                      <View style={styles.card}>
                        <View flexDirection="row" justifyContent="space-between" alignItems="center">
                          <View flexDirection="row" alignItems="center" gap="xs">
                            <Icon name="settingsCameraOutline" size={24} color={theme.colors.text} />
                            <Text textStyle="sm/medium">Manage alarm settings</Text>
                          </View>
                          <Icon name="chevronRight" size={24} color={theme.colors.textMuted} />
                        </View>
                        <View>
                          <Text textStyle="xs/regular" color="textMuted">
                            Manage your site&apos;s routines, schedule temporary exceptions, and configure alarm
                            responses and notifications.
                          </Text>
                        </View>
                      </View>
                    </PressableScale>
                    <UpcomingExceptions siteId={selectedSiteId} cameraKeys={cameraKeys} />
                  </View>
                : <View gap="3xl">
                    <View gap="xxs">
                      <Text textStyle="md/medium">Select a Site</Text>
                      <Text textStyle="sm/regular" color="textMuted">
                        Choose a site below to view and manage its alarm settings.
                      </Text>
                    </View>

                    {siteGroups.map((group) => (
                      <SiteCard key={group.siteId} group={group} isSelected={false} onPress={handleSiteCardPress} />
                    ))}
                  </View>
                }
              </ScrollView>
              <ManageAlarmSettingsBottomSheet bottomSheetRef={manageSheetRef} />
            </>
          )}
        </DataStateView>
      </ScreenContainer>
    </>
  );
}

const styles = StyleSheet.create((theme) => ({
  card: {
    borderRadius: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    paddingVertical: theme.spacing.xl,
    paddingHorizontal: theme.spacing["3xl"],
    gap: theme.spacing.xs,
  },
}));
